// src/App.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Root application component.
// Handles auth state, data loading, and section routing.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState } from "react";
import { useAuth } from "./hooks/useAuth";
import { useCollection } from "./hooks/useFirestore";

import LoginPage from "./pages/LoginPage";
import Sidebar from "./components/Sidebar";
import DashboardPage from "./pages/DashboardPage";
import BlogsPage from "./pages/BlogsPage";
import MagazinePage from "./pages/MagazinePage";
import GalleryPage from "./pages/GalleryPage";
import AgriKnowledgePage from "./pages/AgriKnowledgePage";
import AuthorsPage from "./pages/AuthorsPage";
import CategoriesPage from "./pages/CategoriesPage";

// Loading spinner shown while Firebase auth initialises
function LoadingScreen() {
  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: "linear-gradient(135deg, #0d2410 0%, #2d7a3a 100%)",
    }}>
      <div style={{ textAlign: "center", color: "#fff" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🌾</div>
        <p style={{ fontSize: 16, opacity: 0.8 }}>Loading AgriPragati CMS…</p>
      </div>
    </div>
  );
}

export default function App() {
  const { user, isAdmin, loading, login, logout } = useAuth();
  const [section, setSection] = useState("dashboard");

  // Real-time Firestore listeners — data auto-updates across all tabs
  const { data: blogs }      = useCollection("blogs",        "createdAt");
  const { data: magazines }  = useCollection("magazines",    "createdAt");
  const { data: gallery }    = useCollection("gallery",      "createdAt");
  const { data: agriVideos } = useCollection("agriknowledge","createdAt");
  const { data: authors }    = useCollection("authors",      "createdAt");
  const { data: categoriesDocs } = useCollection("categories", "createdAt");

  // Flatten category docs to simple string array for Select dropdowns
  const categoryNames = categoriesDocs.map(c => c.name).filter(Boolean);

  // ── Show loading screen while Firebase resolves auth state ────────────────
  if (loading) return <LoadingScreen />;

  // ── Show login page if not authenticated ──────────────────────────────────
  if (!user) return <LoginPage onLogin={login} />;

  // ── Main layout ────────────────────────────────────────────────────────────
  const sharedProps = { isAdmin, categories: categoryNames };

  const renderSection = () => {
    switch (section) {
      case "dashboard":
        return (
          <DashboardPage
            blogs={blogs} magazines={magazines}
            gallery={gallery} agriVideos={agriVideos} authors={authors}
          />
        );
      case "blogs":
        return (
          <BlogsPage
            blogs={blogs} authors={authors}
            {...sharedProps}
          />
        );
      case "magazine":
        return <MagazinePage magazines={magazines} {...sharedProps} />;
      case "gallery":
        return <GalleryPage gallery={gallery} isAdmin={isAdmin} />;
      case "agriknowledge":
        return <AgriKnowledgePage agriVideos={agriVideos} isAdmin={isAdmin} />;
      case "authors":
        return <AuthorsPage authors={authors} isAdmin={isAdmin} />;
      case "categories":
        return <CategoriesPage categories={categoriesDocs} isAdmin={isAdmin} />;
      default:
        return null;
    }
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#f5f7f5" }}>
      {/* Left sidebar */}
      <Sidebar
        user={user}
        isAdmin={isAdmin}
        activeSection={section}
        onSection={setSection}
        onLogout={logout}
      />

      {/* Main content area */}
      <main style={{
        flex: 1, padding: "28px 32px", overflowY: "auto", minWidth: 0,
      }}>
        {renderSection()}
      </main>
    </div>
  );
}
